/**
 * @fileoverview i18n Provider Component
 * 
 * React Context Provider for managing translation state across the application.
 * Handles loading, caching, and switching between locales.
 * 
 * @module i18n/context
 * @version 1.0.0
 * @since 2025-07-09
 */

import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  Locale,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  loadLocaleMessages,
  isValidLocale,
  detectBrowserLocale
} from '../index';

// Interface pour le contexte i18n
export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  messages: Record<string, any>;
  isLoading: boolean;
  error: string | null;
}

// Créer le contexte
const I18nContext = createContext<I18nContextValue | null>(null);

// Props du provider
export interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
  enablePersistence?: boolean;
}

// Clé pour la persistance dans localStorage
const LOCALE_STORAGE_KEY = 'app_locale';

/**
 * Provider i18n pour l'application React
 * 
 * Gère le chargement, la mise en cache et le changement de langues.
 * Supporte la persistance dans localStorage.
 * 
 * @param props - Props du provider
 * @returns Provider React avec contexte i18n
 * 
 * @example
 * ```tsx
 * // Dans App.tsx
 * function App() {
 *   return (
 *     <I18nProvider initialLocale="en" enablePersistence={true}>
 *       <MyApp />
 *     </I18nProvider>
 *   );
 * }
 * ```
 */
export const I18nProvider: React.FC<I18nProviderProps> = ({ 
  children, 
  initialLocale,
  enablePersistence = true 
}) => {
  // État des traductions
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
  const [messages, setMessages] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Cache des traductions pour éviter les rechargements
  const [messagesCache, setMessagesCache] = useState<Record<Locale, Record<string, any>>>({} as Record<Locale, Record<string, any>>);
  
  /**
   * Charge les traductions pour une locale donnée
   */
  const loadMessages = useCallback(async (targetLocale: Locale) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Vérifier le cache d'abord
      if (messagesCache[targetLocale]) {
        setMessages(messagesCache[targetLocale]);
        setIsLoading(false);
        return;
      }
      
      // Charger depuis les fichiers
      const loadedMessages = await loadLocaleMessages(targetLocale);
      
      // Mettre à jour le cache
      setMessagesCache(prev => ({
        ...prev,
        [targetLocale]: loadedMessages
      }));
      
      setMessages(loadedMessages);
      
    } catch (err) {
      const errorMessage = `Failed to load messages for locale "${targetLocale}"`;
      console.error(errorMessage, err);
      setError(errorMessage);
      
      // En cas d'erreur, essayer le fallback
      if (targetLocale !== FALLBACK_LOCALE) {
        await loadMessages(FALLBACK_LOCALE);
      }
    } finally {
      setIsLoading(false);
    }
  }, [messagesCache]);
  
  /**
   * Change la locale active
   */
  const setLocale = useCallback((newLocale: Locale) => {
    if (!isValidLocale(newLocale)) {
      console.warn(`Invalid locale "${newLocale}", falling back to ${DEFAULT_LOCALE}`);
      newLocale = DEFAULT_LOCALE;
    }
    
    setLocaleState(newLocale);
    
    // Sauvegarder dans localStorage si activé
    if (enablePersistence) {
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
      } catch (err) {
        console.warn('Failed to save locale to localStorage:', err);
      }
    }
  }, [enablePersistence]);
  
  /**
   * Détermine la locale initiale
   */
  const determineInitialLocale = useCallback((): Locale => {
    // 1. Locale fournie explicitement
    if (initialLocale && isValidLocale(initialLocale)) {
      return initialLocale;
    }
    
    // 2. Locale sauvegardée dans localStorage
    if (enablePersistence) {
      try {
        const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
        if (savedLocale && isValidLocale(savedLocale)) {
          return savedLocale;
        }
      } catch (err) {
        console.warn('Failed to read locale from localStorage:', err);
      }
    }

    // 3. Detection via navigator.language (falls back to DEFAULT_LOCALE if unsupported)
    return detectBrowserLocale();
  }, [initialLocale, enablePersistence]);
  
  // Effet d'initialisation
  useEffect(() => {
    const initLocale = determineInitialLocale();
    setLocaleState(initLocale);
  }, [determineInitialLocale]);
  
  // Effet de chargement des messages quand la locale change
  useEffect(() => {
    loadMessages(locale);
  }, [locale, loadMessages]);
  
  // Valeur du contexte
  const contextValue: I18nContextValue = {
    locale,
    setLocale,
    messages,
    isLoading,
    error
  };
  
  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

// Export par défaut
export default I18nProvider;

// Export du contexte pour utilisation directe si nécessaire  
export { I18nContext };